import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const UserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);

const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

const BG_IMAGE = 'https://images.unsplash.com/photo-1556333376-49e555694a41?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=1400';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [hasCachedSession, setHasCachedSession] = useState(false);

  // Detect offline and cached session
  React.useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check for cached session
    const savedUser = localStorage.getItem('user');
    const savedToken = localStorage.getItem('token');
    setHasCachedSession(!!(savedUser && savedToken));

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleOfflineAccess = () => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        JSON.parse(savedUser);
        // Trigger login which will fallback to cached session
        login('offline', 'offline').then(result => {
          if (result.success) navigate('/offline');
        });
      } catch {}
    }
  };

  // Password recovery state
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryStep, setRecoveryStep] = useState(1); // 1=email, 2=code, 3=done
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryMsg, setRecoveryMsg] = useState('');
  const [recoveryError, setRecoveryError] = useState('');

  const API = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(formData.email, formData.password);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleForgotPassword = () => {
    setShowRecovery(true);
    setRecoveryStep(1);
    setRecoveryEmail('');
    setRecoveryCode('');
    setNewPassword('');
    setRecoveryMsg('');
    setRecoveryError('');
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setRecoveryError('');
    setRecoveryLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: recoveryEmail }),
      });
      const data = await res.json();
      setRecoveryMsg(data.message || 'Codigo enviado');
      setRecoveryStep(2);
    } catch {
      setRecoveryError('Error de conexion. Intenta de nuevo.');
    }
    setRecoveryLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setRecoveryError('');
    if (newPassword.length < 6) {
      setRecoveryError('La contrasena debe tener al menos 6 caracteres.');
      return;
    }
    setRecoveryLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: recoveryEmail, code: recoveryCode, new_password: newPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        setRecoveryStep(3);
        setRecoveryMsg(data.message);
      } else {
        setRecoveryError(data.detail || 'Error al cambiar la contrasena.');
      }
    } catch {
      setRecoveryError('Error de conexion. Intenta de nuevo.');
    }
    setRecoveryLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden" style={{ fontFamily: "'Montserrat', 'Inter', sans-serif" }}>
      {/* Subtle green gradient accent - bottom right corner */}
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] pointer-events-none" style={{
        background: 'radial-gradient(ellipse at 100% 100%, rgba(46,125,50,0.08) 0%, rgba(46,125,50,0.03) 40%, transparent 70%)',
      }} />

      {/* Main content area */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-8 bg-white relative z-10">
        {/* Logo */}
        <div className="mb-8 text-center" data-testid="login-logo">
          <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight select-none">
            <span style={{ color: '#1a1a1a' }}>TKorve</span>
            <span style={{
              background: 'linear-gradient(135deg, #2E7D32, #66BB6A)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>X</span>
          </h1>
          <p className="mt-2 text-sm md:text-base font-medium tracking-[0.15em] text-gray-500" style={{ fontWeight: 500 }}>
            Decisiones que generan valor
          </p>
        </div>

        {/* Login Card */}
        <div className="w-full max-w-sm" data-testid="login-card">
          <div className="bg-white rounded-xl p-7 md:p-8" style={{ boxShadow: '0 4px 32px rgba(0,0,0,0.06), 0 1px 4px rgba(0,0,0,0.04)' }}>

            {/* Offline banner */}
            {isOffline && (
              <div className="mb-5 p-4 bg-amber-50 border border-amber-300 rounded-lg" data-testid="offline-banner">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m-3.536-3.536a5 5 0 010-7.072M12 12h.01" /></svg>
                  <span className="text-sm font-bold text-amber-800">Sin conexion a internet</span>
                </div>
                {hasCachedSession ? (
                  <div>
                    <p className="text-xs text-amber-700 mb-3">Tenes una sesion guardada. Podes acceder al modo offline con los datos cacheados.</p>
                    <button
                      onClick={handleOfflineAccess}
                      className="w-full py-2.5 rounded-lg text-white font-semibold text-sm"
                      style={{ background: 'linear-gradient(180deg, #F59E0B, #D97706)' }}
                      data-testid="offline-access-btn"
                    >
                      Continuar en Modo Campo (Offline)
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-amber-700">Necesitas conectarte a internet para iniciar sesion por primera vez.</p>
                )}
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="mb-5 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm font-medium" data-testid="login-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email field */}
              <div>
                <label htmlFor="email" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Usuario
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                    <UserIcon />
                  </span>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="correo@ejemplo.com"
                    autoComplete="email"
                    required
                    className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 outline-none transition-all duration-200 focus:bg-white focus:border-green-400 focus:ring-2 focus:ring-green-100"
                    data-testid="email-input"
                  />
                </div>
              </div>

              {/* Password field */}
              <div>
                <label htmlFor="password" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Contrasena
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                    <LockIcon />
                  </span>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Tu contrasena"
                    autoComplete="current-password"
                    required
                    className="w-full pl-11 pr-11 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 outline-none transition-all duration-200 focus:bg-white focus:border-green-400 focus:ring-2 focus:ring-green-100"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-0.5"
                    aria-label={showPassword ? 'Ocultar contrasena' : 'Mostrar contrasena'}
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-lg text-white font-semibold text-sm tracking-wide transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: loading ? '#aaa' : 'linear-gradient(180deg, #43A047, #2E7D32)',
                  boxShadow: loading ? 'none' : '0 2px 8px rgba(46,125,50,0.3)',
                }}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.boxShadow = '0 4px 16px rgba(46,125,50,0.4)'; }}
                onMouseLeave={(e) => { if (!loading) e.currentTarget.style.boxShadow = '0 2px 8px rgba(46,125,50,0.3)'; }}
                data-testid="login-button"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" strokeDasharray="32" strokeLinecap="round" /></svg>
                    Ingresando...
                  </span>
                ) : 'Ingresar'}
              </button>
            </form>

            {/* Forgot password */}
            <div className="text-center mt-5">
              <button onClick={handleForgotPassword} className="text-xs text-gray-400 hover:text-green-700 transition-colors font-medium" data-testid="forgot-password">
                Olvidaste tu contrasena?
              </button>
            </div>
          </div>

          {/* Register link */}
          <div className="text-center mt-5">
            <p className="text-sm text-gray-500">
              No tenes cuenta?{' '}
              <Link to="/register" className="text-green-700 hover:text-green-800 font-semibold transition-colors" data-testid="link-to-register">
                Registrate gratis
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Password Recovery Modal */}
      {showRecovery && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowRecovery(false)}>
          <div className="bg-white rounded-xl p-6 md:p-8 w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="recovery-modal">
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-gray-800">
                {recoveryStep === 1 && 'Recuperar contrasena'}
                {recoveryStep === 2 && 'Ingresa el codigo'}
                {recoveryStep === 3 && 'Contrasena cambiada'}
              </h2>
              <button onClick={() => setShowRecovery(false)} className="text-gray-400 hover:text-gray-600 text-xl font-bold" data-testid="close-recovery">&times;</button>
            </div>

            {recoveryError && (
              <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="recovery-error">{recoveryError}</div>
            )}

            {/* Step 1: Enter email */}
            {recoveryStep === 1 && (
              <form onSubmit={handleSendCode}>
                <p className="text-sm text-gray-500 mb-4">Ingresa tu email y te enviaremos un codigo de verificacion.</p>
                <input
                  type="email"
                  value={recoveryEmail}
                  onChange={(e) => setRecoveryEmail(e.target.value)}
                  placeholder="correo@ejemplo.com"
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:border-green-400 focus:ring-2 focus:ring-green-100 mb-4"
                  data-testid="recovery-email-input"
                />
                <button
                  type="submit"
                  disabled={recoveryLoading}
                  className="w-full py-3 rounded-lg text-white font-semibold text-sm transition-all disabled:opacity-60"
                  style={{ background: 'linear-gradient(180deg, #43A047, #2E7D32)' }}
                  data-testid="send-code-button"
                >
                  {recoveryLoading ? 'Enviando...' : 'Enviar codigo'}
                </button>
              </form>
            )}

            {/* Step 2: Enter code + new password */}
            {recoveryStep === 2 && (
              <form onSubmit={handleResetPassword}>
                <p className="text-sm text-gray-500 mb-1">Enviamos un codigo a:</p>
                <p className="text-sm font-semibold text-green-700 mb-4">{recoveryEmail}</p>
                <input
                  type="text"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value)}
                  placeholder="Codigo de 6 digitos"
                  maxLength={6}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-center tracking-[6px] font-bold outline-none focus:border-green-400 focus:ring-2 focus:ring-green-100 mb-3"
                  data-testid="recovery-code-input"
                />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Nueva contrasena (min. 6 caracteres)"
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:border-green-400 focus:ring-2 focus:ring-green-100 mb-4"
                  data-testid="new-password-input"
                />
                <button
                  type="submit"
                  disabled={recoveryLoading}
                  className="w-full py-3 rounded-lg text-white font-semibold text-sm transition-all disabled:opacity-60"
                  style={{ background: 'linear-gradient(180deg, #43A047, #2E7D32)' }}
                  data-testid="reset-password-button"
                >
                  {recoveryLoading ? 'Cambiando...' : 'Cambiar contrasena'}
                </button>
                <button
                  type="button"
                  onClick={() => { setRecoveryStep(1); setRecoveryError(''); }}
                  className="w-full mt-2 py-2 text-sm text-gray-500 hover:text-green-700 transition-colors"
                  data-testid="resend-code-button"
                >
                  Reenviar codigo
                </button>
              </form>
            )}

            {/* Step 3: Success */}
            {recoveryStep === 3 && (
              <div className="text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                </div>
                <p className="text-sm text-gray-600 mb-5">{recoveryMsg}</p>
                <button
                  onClick={() => setShowRecovery(false)}
                  className="w-full py-3 rounded-lg text-white font-semibold text-sm"
                  style={{ background: 'linear-gradient(180deg, #43A047, #2E7D32)' }}
                  data-testid="back-to-login-button"
                >
                  Volver al login
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bottom image band */}
      <div className="relative h-36 md:h-44 lg:h-52 w-full flex-shrink-0" data-testid="login-image-band">
        {/* Gradient overlay to blend with white above */}
        <div className="absolute inset-0 z-10" style={{
          background: 'linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(255,255,255,0.7) 20%, rgba(255,255,255,0) 50%)',
        }} />
        <img
          src={BG_IMAGE}
          alt="Ganado en campo verde"
          className="w-full h-full object-cover object-center"
          loading="lazy"
        />
      </div>

      {/* Footer bar */}
      <div className="bg-gray-50 border-t border-gray-100 py-3 text-center flex-shrink-0 relative z-10">
        <p className="text-[11px] text-gray-400 font-medium tracking-wide">
          Plataforma de gestion ganadera inteligente
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
